import { z } from 'zod';

import { BooleanParamSchema } from '../../common/boolean-param/boolean-param';
import { IdsParamSchema } from '../../common/ids-param/ids-param';
import { makeListQuery } from '../../common/list-query/list-query';

import { GenderSchema } from '../gender';
import { UserStatusSchema } from '../user-status';

/**
 * The whitelisted filters for the staff member list (N7). Anything not here is
 * rejected by `.strict()` before it can reach a column, and the resource's
 * `FilterSpec` decides which column and operator each one actually maps to —
 * soft-delete scoping is expressible in neither.
 */
export const UsersListQuerySchema = makeListQuery({
  search: z.string().min(1).max(200).optional(),
  status: UserStatusSchema.optional(),
  gender: GenderSchema.optional(),
  isHidden: BooleanParamSchema.optional(),
  /** Resolve-by-ids for the admin `ServerSelect` — see `IdsParamSchema`. */
  ids: IdsParamSchema.optional(),
  /**
   * Accounts holding **any** role other than `member` — i.e. staff (the `member`
   * role carries zero catalog permissions by rule, N8).
   *
   * The admin's staff list is the honest use: without this filter the surface at
   * `/admins/staff` lists every registered account, which is what it did from P9
   * until P10.
   */
  isStaff: BooleanParamSchema.optional(),
  /**
   * Accounts holding at least one of these roles, by role **id** — the shape a
   * `ServerSelect` already produces and resolves labels for.
   *
   * Ids rather than keys on purpose: `access.user_roles` stores the id, so a key
   * would need a second lookup to mean anything, and a renamed key would
   * silently stop matching.
   */
  roleIds: IdsParamSchema.optional(),
});

/**
 * `roleKey` was removed here on 2026-07-28 (P9) and **replaced** by `roleIds`
 * plus `isStaff` in P10. The history stays because the failure it records is the
 * one N7 exists to prevent.
 *
 * `roleKey` was declared in this schema but had no entry in `USER_FILTER_SPEC`,
 * and `applyFilters` iterates the **spec**, not the incoming values — so
 * `?roleKey=reviewer` was accepted, validated, and then silently ignored. A
 * whitelisted filter the endpoint does not perform tells a client the list is
 * narrowed when it is not.
 *
 * Doing it correctly needed a capability the shared query builder did not have:
 * role membership lives in `access.user_roles`, and the only shapes `FilterSpec`
 * could express were a column condition and an OR group — so the filter would
 * have had to materialise *every user id holding that role* into an `IN` list, on
 * the largest table in the platform. P10 added the third shape instead: the
 * correlated `ExistsFilter` in `shared/repository`, which both filters above use.
 */

export type UsersListQuery = z.infer<typeof UsersListQuerySchema>;
